import axios from "axios";
import { config } from "../config/environment";
import refreshTokenManager from "./refreshTokenManager";

// Types for authentication
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role?: string;
  department?: string;
  employee_id?: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      username: string;
      email: string;
      first_name: string;
      last_name: string;
      role: string;
      phone?: string;
      department?: string;
      employee_id?: string;
    };
    token: string;
    refreshToken: string;
    expiresIn?: string;
  };
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  password: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

// Create axios instance with base configuration
const authApi = axios.create({
  baseURL: config.API_URL,
  timeout: config.REQUEST_TIMEOUT,
  withCredentials: config.USE_COOKIES, // Enable cookies for HttpOnly tokens
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor to add auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid refresh logic for auth endpoints themselves (login/register/refresh/etc.)
    const requestUrl: string = originalRequest?.url || "";
    const isAuthEndpoint = [
      "/auth/login",
      "/auth/register",
      "/auth/refresh",
      "/auth/forgot-password",
      "/auth/reset-password",
      "/auth/change-password",
      "/auth/me",
    ].some((path) => requestUrl.includes(path));

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      try {
        // Skip refresh if no refresh token is available
        const hasRefreshToken = !!localStorage.getItem("refreshToken");
        if (!hasRefreshToken) {
          return Promise.reject(error);
        }
        // Use centralized refresh manager to prevent multiple simultaneous refresh calls
        const newToken = await refreshTokenManager.refreshToken();
        
        if (newToken) {
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return authApi(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // The refresh manager already handles cleanup and redirect
      }
    }

    return Promise.reject(error);
  }
);

class AuthService {
  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await authApi.post("/auth/login", credentials);
    return response.data;
  }

  // Register user
  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await authApi.post("/auth/register", userData);
    return response.data;
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await authApi.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear local storage regardless of API call success
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    }
  }

  // Forgot password
  async forgotPassword(
    data: ForgotPasswordData
  ): Promise<{ success: boolean; data: any }> {
    const response = await authApi.post("/auth/forgot-password", data);
    return response.data;
  }

  // Reset password
  async resetPassword(
    data: ResetPasswordData
  ): Promise<{ success: boolean; data: any }> {
    const response = await authApi.post("/auth/reset-password", data);
    return response.data;
  }

  // Change password
  async changePassword(
    data: ChangePasswordData
  ): Promise<{ success: boolean; data: any }> {
    const response = await authApi.post("/auth/change-password", data);
    return response.data;
  }

  // Get current user profile
  async getProfile(): Promise<{ success: boolean; data: any }> {
    const response = await authApi.get("/auth/me");
    return response.data;
  }

  // Refresh token
  async refreshToken(
    refreshToken: string
  ): Promise<{ success: boolean; data: any }> {
    const response = await authApi.post("/auth/refresh", { refreshToken });
    return response.data;
  }

  // Get user permissions
  async getUserPermissions(): Promise<{ success: boolean; data: any }> {
    const response = await authApi.get("/roles/my-permissions");
    return response.data;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem("token");
    return !!token;
  }

  // Get stored token
  getToken(): string | null {
    return localStorage.getItem("token");
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  // Get stored user data
  getUser(): any {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  // Store authentication data
  storeAuthData(data: AuthResponse["data"]): void {
    localStorage.setItem("token", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    localStorage.setItem("user", JSON.stringify(data.user));
  }

  // Clear authentication data
  clearAuthData(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }
}

export const authService = new AuthService();
export default authService;
